import React from "react";
import type { V3TimeFilter } from "./performanceChartV3Utils";
import type { StrategyComparisonChartDataResult } from "../hooks/useStrategyComparisonChartData";
import { useD3LineChart } from "../hooks/useD3LineChart";
import { formatPercentage, formatSignedPercentage } from "../utils/numberUtils";

type StrategyComparisonChartContentProps = Pick<
  StrategyComparisonChartDataResult,
  | "loading"
  | "error"
  | "chartData"
  | "lines"
  | "chartHeight"
  | "timeFilterOptions"
  | "availableFilters"
  | "selectedPeriod"
  | "handleSelectPeriod"
> & {
  isDarkMode?: boolean;
  onRetry?: () => void;
  showUnavailablePeriods?: boolean;
};

function getResponsiveChartLayout(chartHeight: number) {
  if (typeof window !== "undefined" && window.innerWidth < 640) {
    return {
      height: Math.min(chartHeight, 360),
      margins: { top: 8, right: 54, bottom: 20, left: 8 },
      watermarkBottomClass: "bottom-[70px]",
    };
  }

  return {
    height: chartHeight,
    margins: { top: 8, right: 72, bottom: 24, left: 12 },
    watermarkBottomClass: "bottom-[86px]",
  };
}

const StrategyComparisonChartContent: React.FC<StrategyComparisonChartContentProps> = ({
  loading,
  error,
  chartData,
  lines,
  chartHeight,
  timeFilterOptions,
  availableFilters,
  selectedPeriod,
  handleSelectPeriod,
  isDarkMode = true,
  onRetry,
  showUnavailablePeriods = false,
}) => {
  const [layout, setLayout] = React.useState(() => getResponsiveChartLayout(chartHeight));
  const displayLines = React.useMemo(
    () => isDarkMode
      ? lines
      : lines.map((line) => line.stroke === "white" ? { ...line, stroke: "currentColor" } : line),
    [isDarkMode, lines]
  );

  React.useEffect(() => {
    const updateLayout = () => {
      setLayout(getResponsiveChartLayout(chartHeight));
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [chartHeight]);

  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: chartData,
    lines: displayLines,
    isDarkMode,
    height: layout.height,
    margins: layout.margins,
    yAxisFormatter: (value: number) => formatPercentage(value),
    tooltipFormatter: (value: number) => formatSignedPercentage(value, 2),
    showLegend: false,
    renderingMode: "enhanced",
    useUtcDateLabels: true,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${isDarkMode ? "border-slate-400" : "border-v7-bronze-dark"}`} />
        <span className={`ml-3 text-sm ${isDarkMode ? "text-gray-300" : "text-v7-ink/60"}`}>Loading comparison data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg border p-4 text-center ${isDarkMode ? "border-red-800 bg-red-900/20" : "border-red-200 bg-red-50"}`}>
        <p className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-700"}`}>{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={`mt-3 rounded-v7-control px-3 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
              isDarkMode
                ? "bg-slate-200 text-black focus-visible:outline-slate-300"
                : "bg-v7-ink text-v7-white focus-visible:outline-v7-bronze-dark"
            }`}
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (chartData.length === 0 || lines.length === 0) {
    return (
      <div className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-v7-ink/60"}`}>
        <p>No comparison data available for the current selection.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isDarkMode ? "" : "text-v7-ink"}`}>
      <div className={`mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 text-xs ${isDarkMode ? "text-gray-300" : "text-v7-ink-soft"}`}>
        {displayLines.map((line) => (
          <div key={line.dataKey} className="flex max-w-[220px] items-center gap-2">
            <span
              className={`h-0.5 w-5 flex-shrink-0 rounded-full ${
                !isDarkMode && line.stroke === "currentColor" ? "text-v7-ink" : ""
              }`}
              style={{ backgroundColor: line.stroke }}
            />
            <span className="truncate">{line.label}</span>
          </div>
        ))}
      </div>

      <div
        ref={containerRef}
        className="relative overflow-visible"
        style={{ width: "100%", height: layout.height }}
      >
        <svg ref={svgRef} className="block" />
        <div
          ref={tooltipRef}
          className={`pointer-events-none absolute z-[1000] hidden rounded-md border px-3 py-2 shadow-lg ${
            isDarkMode ? "border-slate-700 bg-slate-950" : "border-v7-ink/15 bg-v7-white text-v7-ink"
          }`}
        />
        <div className={`absolute pointer-events-none select-none left-3 ${layout.watermarkBottomClass} z-[5]`}>
          <img
            data-testid="strategy-comparison-chart-watermark-wordmark"
            src={isDarkMode ? "/dark.png" : "/logo.png"}
            alt="Plutus"
            className="h-8 w-auto"
          />
        </div>
      </div>

      <div className="mt-2 px-1 sm:px-2">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-medium">
          {timeFilterOptions
            .filter(item => showUnavailablePeriods || availableFilters.includes(item.value))
            .map(item => (
              <button
                key={item.value}
                onClick={() => handleSelectPeriod(item.value as V3TimeFilter)}
                aria-pressed={selectedPeriod === item.value}
                disabled={!availableFilters.includes(item.value)}
                className={`border px-2.5 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${isDarkMode ? "rounded-full focus:ring-slate-300" : "rounded-v7-control focus:ring-v7-bronze-dark"} ${
                  !availableFilters.includes(item.value)
                    ? isDarkMode
                      ? "cursor-not-allowed border-slate-800 text-slate-600"
                      : "cursor-not-allowed border-v7-ink/15 text-v7-ink/30"
                    : selectedPeriod === item.value
                    ? isDarkMode
                      ? "border-slate-200 bg-slate-200 text-black shadow-sm"
                      : "border-v7-ink bg-v7-ink text-v7-white shadow-sm"
                    : isDarkMode
                      ? "border-transparent text-slate-300 hover:bg-slate-900 hover:text-white"
                      : "border-transparent text-v7-ink-soft hover:bg-v7-paper hover:text-v7-ink"
                }`}
              >
                {item.label}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default StrategyComparisonChartContent;
